import AddCategory from './AddCategory';
import './CategoryDefinition.css';
import CategoryItem from './CategoryItem';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../authContext';
import ConfirmDeleteModal from '../../Database/ListaDatabase/ConfirmDeleteModal';


function CategoryDefinition({ classeCSS, assistantId }) {
    const { t } = useTranslation(), { t: tCommon } = useTranslation('common');
    const { accessToken } = useContext(AuthContext);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState(null); // Categoria temporária antes de ser salva

    const normalizeCategoryPositions = (list) => list.map((category, index) => ({
        ...category,
        position: String(index + 1),
    }));

    const sendBulkReorderPayload = async (orderedCategories) => {
        const payload = {
            assistant_id: assistantId,
            list_categories: orderedCategories.map((category) => ({
                id: String(category.id),
                name: category.name,
                description: category.description,
                position: String(category.position),
            })),
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/put_ordering`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`Falha no reorder em lote (${response.status})`);
        }
    };

    const handleMoveCategory = async (currentIndex, direction) => {
        if (currentIndex < 0 || currentIndex >= categories.length) {
            return;
        }

        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= categories.length) {
            return;
        }

        const reordered = [...categories];
        const [movedCategory] = reordered.splice(currentIndex, 1);
        reordered.splice(targetIndex, 0, movedCategory);

        const normalized = normalizeCategoryPositions(reordered);

        setCategories(normalized);

        try {
            await sendBulkReorderPayload(normalized);
        } catch {}
    };

    const handleOpenDeleteModal = (assistantId, categoryId) => {
        setCategoryToDelete({ assistantId, categoryId });
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        handleDeleteCategory(categoryToDelete.assistantId, categoryToDelete.categoryId);
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
    };


    // Cria um novo item para edição antes de salvar
    const handleAddCategory = () => {
        setNewCategory({ name: "", description: "" });
    };

    // Salva a nova categoria na lista
    const handleSaveNewCategory = (category) => {
        setCategories([...categories, category]); // Adiciona a categoria salva à lista
        setNewCategory(null); // Remove a categoria temporária
    };

    // Cancela a criação da nova categoria
    const handleCancelNewCategory = () => {
        setNewCategory(null); // Descarta a categoria temporária
    };

    const handleDeleteCategory = async (assistantIdToDelete, categoryIdToDelete) => {
        setCategories(categories.filter((cat) => cat.id !== categoryIdToDelete));
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_category`, {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    assistant_id: assistantIdToDelete,
                    category_id: categoryIdToDelete
                }),
            });

            const data = await response.json();

            if (data.status) {
                // Sucesso, nenhuma ação adicional necessária.
            } else {
                console.error("Erro ao excluir categoria.");
            }
        } catch (error) {
            console.error("Erro na comunicação com o servidor:", error);
        }
    };

    const fetchCategories = useCallback(() => {
        if (!assistantId || !accessToken) {
            setCategories([]);
            return;
        }

        fetch(`${process.env.REACT_APP_API_URL}/post_categories`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ assistant_id: assistantId }),
        })
            .then(async (response) => {
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
                    throw new Error(errorData.message || 'Erro na requisição');
                }
                return response.json();
            })
            .then(data => {
                if (data.list_categories) {
                    setCategories(data.list_categories);
                }
            })
            .catch(error => {
                console.error("Erro ao buscar categorias:", error);
            });
    }, [assistantId, accessToken]);

    // 🚀 Busca inicial das categorias do assistente
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);


    return (
        <div className="category-definition-wrapper">
            
            {/* Cabeçalho com Título e Botão fora da caixa branca */}
            <div className="category-header">
                <p className="category-title">{tCommon('dataDefinition')}</p>
                <AddCategory onClick={handleAddCategory} />
            </div>

            <div className={classeCSS}>
                <div className='category-definition-scrollable'>

                    {/* Renderiza uma nova categoria somente se estiver em edição */}
                    {newCategory && (
                        <CategoryItem
                            categoryName={newCategory.name}
                            categoryDescription={newCategory.description}
                            isNew={true} // Flag para estilos diferenciados
                            onSave={handleSaveNewCategory}
                            onCancel={handleCancelNewCategory}
                            onDelete={handleCancelNewCategory}
                            assistantId={assistantId}
                            setCategories={setCategories}
                            showUp={false}
                            showDown={false}
                        />
                    )}

                    {categories.map((category, index) => (
                        <CategoryItem
                            key={category.id}
                            categoryName={category.name}
                            categoryDescription={category.description}
                            assistantId={assistantId}
                            categoryId={category.id}
                            categoryPosition={category.position ?? String(index + 1)}
                            onDelete={() => handleOpenDeleteModal(assistantId, category.id)}
                            showUp={index > 0}
                            showDown={index < categories.length - 1}
                            onMoveUp={() => handleMoveCategory(index, 'up')}
                            onMoveDown={() => handleMoveCategory(index, 'down')}
                        />
                    ))}

                </div>
            </div>

            <ConfirmDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
            />

        </div>
    );
}

export default CategoryDefinition;